import { Group, Stack } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';

import type { IColumn, TTheme } from '@feathr/components';
import { AvatarV2 as Avatar, ContextMenu, TableColumnHeader, Text, Time } from '@feathr/components';
import { getIconForAction, TimeFormat } from '@feathr/hooks';

import DemoMenuItem from '../components/DemoMenuItem';
import type { ISamplePerson } from '../sampleData';
import buildOptionsColumn from './buildOptionsColumn';
import buildPillColumn from './buildPillColumn';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';

const STATUS_THEME: Record<ISamplePerson['status'], TTheme> = {
  identified: 'neutral',
  subscribed: 'green',
  unsubscribed: 'red',
};

const STATUS_LABEL: Record<ISamplePerson['status'], string> = {
  identified: 'Identified',
  subscribed: 'Subscribed',
  unsubscribed: 'Unsubscribed',
};

const ENGAGEMENT_THEME: Record<ISamplePerson['engagement'], TTheme> = {
  high: 'green',
  medium: 'yellow',
  low: 'neutral',
};

const ENGAGEMENT_LABEL: Record<ISamplePerson['engagement'], string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

interface IPeopleColumnsArgs {
  base: string;
  t: TFunction;
}

function peopleColumns({ base, t }: IPeopleColumnsArgs): Array<IColumn<ISamplePerson>> {
  return [
    {
      accessor: 'name',
      id: 'name',
      Header: TableColumnHeader({ title: t('Name'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      minWidth: 280,
      Cell({ original }): JSX.Element {
        return (
          <Group gap={'sm'} wrap={'nowrap'}>
            <Avatar name={original.name} placeholder={{ name: original.name }} size={'small'} />
            <Stack gap={0}>
              <Link to={base}>{original.name}</Link>
              <Text c={'var(--color-neutral-600)'} variant={'smallRegular'}>
                {original.email}
              </Text>
            </Stack>
          </Group>
        );
      },
    },
    buildPillColumn<ISamplePerson, ISamplePerson['status']>({
      accessor: 'status',
      labelMap: STATUS_LABEL,
      t,
      themeMap: STATUS_THEME,
      title: t('Status'),
    }),
    buildPillColumn<ISamplePerson, ISamplePerson['engagement']>({
      accessor: 'engagement',
      labelMap: ENGAGEMENT_LABEL,
      t,
      themeMap: ENGAGEMENT_THEME,
      title: t('Engagement'),
    }),
    {
      accessor: 'lastSeen',
      id: 'lastSeen',
      Header: TableColumnHeader({ title: t('Last seen'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellCenter,
      sortable: true,
      width: 160,
      Cell({ original }): JSX.Element {
        return <Time format={TimeFormat.timeFromNow} timestamp={original.lastSeen} />;
      },
    },
    buildOptionsColumn<ISamplePerson>({
      t,
      renderItems: (): JSX.Element => (
        <>
          <ContextMenu.Item link={base} prefix={getIconForAction('edit')}>
            {t('View profile')}
          </ContextMenu.Item>
          <DemoMenuItem action={'add'} label={t('Add to segment')} />
          <DemoMenuItem action={'archive'} isDestructive={true} label={t('Archive')} />
        </>
      ),
    }),
  ];
}

export default peopleColumns;
